import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGuard from "@/components/RoleGuard";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import AgentLogin from "@/pages/agent/AgentLogin";
import AgentSelectCampaign from "@/pages/agent/AgentSelectCampaign";
import AgentWorkspace from "@/pages/agent/AgentWorkspace";
import AgentProtectedRoute from "@/components/AgentProtectedRoute";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Resellers from "@/pages/Resellers";
import Users from "@/pages/Users";
import Pricing from "@/pages/Pricing";
import Wallets from "@/pages/Wallets";
import Campaigns from "@/pages/Campaigns";
import CampaignEdit from "@/pages/CampaignEdit";
import CampaignLeadsToDial from "@/pages/CampaignLeadsToDial";
import Reports from "@/pages/Reports";
import CDR from "@/pages/CDR";
import DispositionReport from "@/pages/reports/DispositionReport";
import CampaignDispositionReport from "@/pages/reports/CampaignDispositionReport";
import TimesheetReport from "@/pages/reports/TimesheetReport";
import CdrPhoneDetail from "@/pages/reports/CdrPhoneDetail";
import SystemSettings from "@/pages/SystemSettings";
import AudioLibrary from "@/pages/AudioLibrary";
import AdminBranding from "@/pages/AdminBranding";
import OpensipsServers from "@/pages/OpensipsServers";
import MediaServers from "@/pages/MediaServers";
import AIEngines from "@/pages/AIEngines";
import PdServers from "@/pages/PdServers";
import WebRtcServers from "@/pages/WebRtcServers";
import CustomerSettings from "@/pages/CustomerSettings";
import CustomerDispositions from "@/pages/CustomerDispositions";
import CustomerTeam from "@/pages/CustomerTeam";
import CustomerCallbacks from "@/pages/CustomerCallbacks";
import AdminCallbackReconciliation from "@/pages/AdminCallbackReconciliation";
import Tickets from "@/pages/Tickets";
import CustomerLists from "@/pages/CustomerLists";
import CustomerListDetail from "@/pages/CustomerListDetail";
import CustomerLeads from "@/pages/CustomerLeads";
import CustomerVendors from "@/pages/CustomerVendors";
import CustomerDids from "@/pages/CustomerDids";
import CustomerInboundGroups from "@/pages/CustomerInboundGroups";
import AICatalog from "@/pages/AICatalog";
import ThirdPartyProviders from "@/pages/ThirdPartyProviders";
import CustomerProviders from "@/pages/CustomerProviders";
import VicidialServers from "@/pages/VicidialServers";
import GlobalTemplates from "@/pages/GlobalTemplates";
import BillingPricing from "@/pages/BillingPricing";
import PredictivePricing from "@/pages/PredictivePricing";
import AiPricing from "@/pages/AiPricing";
import ServerPricing from "@/pages/ServerPricing";
import Installation from "@/pages/Installation";
import WireGuard from "@/pages/WireGuard";
import SshTunnel from "@/pages/SshTunnel";
import AdminPayments from "@/pages/AdminPayments";
import CallLimit from "@/pages/CallLimit";
import AccountBilling from "@/pages/AccountBilling";
import AccountPayments from "@/pages/AccountPayments";
import ResellerPricing from "@/pages/ResellerPricing";
import WhiteLabeling from "@/pages/WhiteLabeling";
import LiveCalls from "@/pages/LiveCalls";
import LiveStats from "@/pages/LiveStats";
import SystemLogs from "@/pages/SystemLogs";
import ImpersonateLanding from "@/pages/ImpersonateLanding";
import StatsReport from "@/pages/StatsReport";
import Commissions from "@/pages/Commissions";
import PaymentHistory from "@/pages/PaymentHistory";
import { ResellerBillingHistory, ResellerPaymentHistory } from "@/pages/ResellerHistory";
import CustomerSpeech from "@/pages/CustomerSpeech";
import KnowledgeBases from "@/pages/KnowledgeBases";
import StaffKnowledgeBases from "@/pages/StaffKnowledgeBases";
import StaffKnowledgeBaseView from "@/pages/StaffKnowledgeBaseView";
import Landing from "@/pages/Landing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Profile from "@/pages/Profile";
import Features from "@/pages/Features";
import Benefits from "@/pages/Benefits";
import KnowledgeBase from "@/pages/KnowledgeBase";
import VoiceAgents from "@/pages/agents/VoiceAgents";
import AIAgents from "@/pages/agents/AIAgents";
import HybridAgents from "@/pages/agents/HybridAgents";
import { useAuth } from "@/context/AuthContext";
import { isAgentAccount } from "@/lib/agentAccount";
import { BrandProvider } from "@/context/BrandContext";
import SeoMeta from "@/components/SeoMeta";

function PaymentHistoryGate() {
  const { user } = useAuth();
  if (user?.role === "reseller") return <ResellerPaymentHistory />;
  return <PaymentHistory channel="direct" />;
}

function ProviderPage({ kind }) {
  const { user } = useAuth();
  if (user?.role === "customer") return <CustomerProviders kind={kind} />;
  return <ThirdPartyProviders kind={kind} />;
}

function RootGate() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  // Agents still enter via their workspace; everyone else gets the public landing at /
  if (user && isAgentAccount(user)) return <Navigate to="/agent/select-campaign" replace />;
  return <Landing />;
}

function AppToaster() {
  const { pathname } = useLocation();
  const isAgentScreen = pathname === "/agent" || pathname.startsWith("/agent/");
  useEffect(() => {
    if (isAgentScreen) toast.dismiss();
  }, [isAgentScreen]);
  if (isAgentScreen) return null;
  return <Toaster position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrandProvider>
        <SeoMeta />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/agent/login" element={<AgentLogin />} />
            <Route path="/agent/select-campaign" element={<AgentProtectedRoute><AgentSelectCampaign /></AgentProtectedRoute>} />
            <Route path="/agent/workspace" element={<AgentProtectedRoute><AgentWorkspace /></AgentProtectedRoute>} />
            {/* /agent, /agent/ and any unknown /agent/* path open the agent login. */}
            <Route path="/agent" element={<Navigate to="/agent/login" replace />} />
            <Route path="/agent/*" element={<Navigate to="/agent/login" replace />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/impersonate" element={<ImpersonateLanding />} />
            <Route path="/agents/voice" element={<VoiceAgents />} />
            <Route path="/agents/ai" element={<AIAgents />} />
            <Route path="/agents/hybrid" element={<HybridAgents />} />
            <Route path="/features" element={<Features />} />
            <Route path="/benefits" element={<Benefits />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/" element={<RootGate />} />
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/profile" element={<Profile />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/customers" element={<RoleGuard allow={["admin", "reseller"]} menu="customers"><Customers /></RoleGuard>} />
              <Route path="/resellers" element={<RoleGuard allow={["admin"]} menu="resellers"><Resellers /></RoleGuard>} />
              <Route path="/users" element={<RoleGuard allow={["admin"]} menu="users"><Users /></RoleGuard>} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/campaigns/:campaignId/edit" element={<RoleGuard allow={["customer"]}><CampaignEdit /></RoleGuard>} />
              <Route path="/campaigns/:campaignId/leads-to-dial" element={<RoleGuard allow={["customer"]}><CampaignLeadsToDial /></RoleGuard>} />
              <Route path="/live-calls" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="live-calls"><LiveCalls /></RoleGuard>} />
              <Route path="/text-to-speech" element={<RoleGuard allow={["customer"]}><CustomerSpeech /></RoleGuard>} />
              <Route path="/knowledge-bases" element={<RoleGuard allow={["customer"]}><KnowledgeBases /></RoleGuard>} />
              <Route path="/staff-knowledge-bases" element={<RoleGuard allow={["admin", "staff"]} menu="staff-knowledge-bases"><StaffKnowledgeBases /></RoleGuard>} />
              <Route path="/staff-knowledge-bases/:kbId" element={<RoleGuard allow={["admin", "staff"]} menu="staff-knowledge-bases"><StaffKnowledgeBaseView /></RoleGuard>} />
              <Route path="/templates" element={<RoleGuard allow={["admin"]} menu="templates"><GlobalTemplates /></RoleGuard>} />
              <Route path="/servers/opensips" element={<RoleGuard allow={["admin"]} menu="servers-opensips"><OpensipsServers /></RoleGuard>} />
              <Route path="/servers/media" element={<RoleGuard allow={["admin"]} menu="servers-media"><MediaServers /></RoleGuard>} />
              <Route path="/servers/ai-engines" element={<RoleGuard allow={["admin"]} menu="servers-ai-engines"><AIEngines /></RoleGuard>} />
              <Route path="/servers/pd" element={<RoleGuard allow={["admin"]} menu="servers-pd"><PdServers /></RoleGuard>} />
              <Route path="/servers/webrtc" element={<RoleGuard allow={["admin"]} menu="servers-webrtc"><WebRtcServers /></RoleGuard>} />
              <Route path="/servers/pricing" element={<RoleGuard allow={["admin"]} menu="servers-pricing"><ServerPricing /></RoleGuard>} />
              <Route path="/installation" element={<RoleGuard allow={["admin"]} menu="installation"><Installation /></RoleGuard>} />
              <Route path="/vpn/wireguard" element={<RoleGuard allow={["admin"]} menu="vpn-wireguard"><WireGuard /></RoleGuard>} />
              <Route path="/vpn/ssh-tunnel" element={<RoleGuard allow={["admin"]} menu="vpn-ssh-tunnel"><SshTunnel /></RoleGuard>} />
              <Route path="/ai-bots/models" element={<RoleGuard allow={["admin"]} menu="ai-bots-models"><AICatalog kind="models" /></RoleGuard>} />
              <Route path="/ai-bots/tts" element={<RoleGuard allow={["admin"]} menu="ai-bots-tts"><AICatalog kind="tts" /></RoleGuard>} />
              <Route path="/ai-bots/stt" element={<RoleGuard allow={["admin"]} menu="ai-bots-stt"><AICatalog kind="stt" /></RoleGuard>} />
              <Route path="/providers/sms" element={<RoleGuard allow={["admin", "customer"]} menu="providers-sms"><ProviderPage kind="sms" /></RoleGuard>} />
              <Route path="/providers/email" element={<RoleGuard allow={["admin", "customer"]} menu="providers-email"><ProviderPage kind="email" /></RoleGuard>} />
              <Route path="/servers/vicidial" element={<RoleGuard allow={["admin", "customer"]} menu="servers-vicidial"><VicidialServers /></RoleGuard>} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/billing/pricing" element={<RoleGuard allow={["admin"]} menu="billing-pricing"><BillingPricing /></RoleGuard>} />
              <Route path="/billing/predictive-pricing" element={<RoleGuard allow={["admin"]} menu="billing-pricing"><PredictivePricing /></RoleGuard>} />
              <Route path="/billing/ai-pricing" element={<RoleGuard allow={["admin"]} menu="billing-pricing"><AiPricing /></RoleGuard>} />
              <Route path="/billing/payments" element={<RoleGuard allow={["admin"]} menu="billing-payments"><AdminPayments /></RoleGuard>} />
              <Route path="/call-limit" element={<RoleGuard allow={["reseller", "customer"]}><CallLimit /></RoleGuard>} />
              <Route path="/team" element={<RoleGuard allow={["customer"]}><CustomerTeam /></RoleGuard>} />
              <Route path="/callbacks" element={<RoleGuard allow={["customer"]}><CustomerCallbacks /></RoleGuard>} />
              <Route path="/callback-reconciliation" element={<RoleGuard allow={["admin", "staff"]} menu="callback-reconciliation"><AdminCallbackReconciliation /></RoleGuard>} />
              <Route path="/tickets" element={<RoleGuard allow={["admin", "staff", "customer"]}><Tickets /></RoleGuard>} />
              <Route path="/account-settings" element={<RoleGuard allow={["customer"]}><CustomerSettings section="leads" /></RoleGuard>} />
              <Route path="/account-smtp" element={<RoleGuard allow={["customer"]}><CustomerSettings section="smtp" /></RoleGuard>} />
              <Route path="/account-dispositions" element={<RoleGuard allow={["customer"]}><CustomerDispositions /></RoleGuard>} />
              <Route path="/account-audios" element={<RoleGuard allow={["customer"]}><AudioLibrary mode="customer" /></RoleGuard>} />
              <Route path="/lists" element={<RoleGuard allow={["customer"]}><CustomerLists /></RoleGuard>} />
              <Route path="/lists/:listId" element={<RoleGuard allow={["customer"]}><CustomerListDetail /></RoleGuard>} />
              <Route path="/leads" element={<RoleGuard allow={["customer"]}><CustomerLeads /></RoleGuard>} />
              <Route path="/routing/vendors" element={<RoleGuard allow={["customer"]}><CustomerVendors /></RoleGuard>} />
              <Route path="/routing/dids" element={<RoleGuard allow={["customer"]}><CustomerDids /></RoleGuard>} />
              <Route path="/routing/inbound-groups" element={<RoleGuard allow={["customer"]}><CustomerInboundGroups /></RoleGuard>} />
              <Route path="/billing" element={<RoleGuard allow={["reseller", "customer"]}><AccountBilling /></RoleGuard>} />
              <Route path="/payments" element={<RoleGuard allow={["reseller", "customer"]}><AccountPayments /></RoleGuard>} />
              <Route path="/reseller-pricing" element={<RoleGuard allow={["reseller"]}><ResellerPricing /></RoleGuard>} />
              <Route path="/white-labeling" element={<RoleGuard allow={["reseller"]}><WhiteLabeling /></RoleGuard>} />
              <Route path="/wallet" element={<RoleGuard allow={["reseller", "customer"]}><Wallets /></RoleGuard>} />
              <Route path="/reports" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="reports"><Reports /></RoleGuard>} />
              <Route path="/reports/live-stats" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="reports-live-stats"><LiveStats /></RoleGuard>} />
              <Route path="/reports/outbound-disposition" element={<RoleGuard allow={["admin", "reseller", "customer"]}><DispositionReport direction="outbound" title="Outbound disposition" /></RoleGuard>} />
              <Route path="/reports/inbound-disposition" element={<RoleGuard allow={["admin", "reseller", "customer"]}><DispositionReport direction="inbound" title="Inbound disposition" /></RoleGuard>} />
              <Route path="/reports/campaign-disposition" element={<RoleGuard allow={["admin", "reseller", "customer"]}><CampaignDispositionReport /></RoleGuard>} />
              <Route path="/reports/timesheet" element={<RoleGuard allow={["admin", "reseller", "customer"]}><TimesheetReport /></RoleGuard>} />
              <Route path="/reports/stats" element={<RoleGuard allow={["admin"]} menu="reports-stats"><StatsReport /></RoleGuard>} />
              <Route path="/reports/payment-history" element={<RoleGuard allow={["admin", "reseller"]} menu="reports-payment-history"><PaymentHistoryGate /></RoleGuard>} />
              <Route path="/reports/reseller-payment" element={<RoleGuard allow={["admin"]} menu="reports-reseller-payment"><PaymentHistory channel="reseller" /></RoleGuard>} />
              <Route path="/reports/commissions" element={<RoleGuard allow={["admin", "staff"]} menu="reports-commissions"><Commissions /></RoleGuard>} />
              <Route path="/reports/billing-history" element={<RoleGuard allow={["reseller"]}><ResellerBillingHistory /></RoleGuard>} />
              <Route path="/cdr" element={<Navigate to="/cdr/outbound" replace />} />
              <Route path="/cdr/outbound" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="cdr"><CDR direction="outbound" /></RoleGuard>} />
              <Route path="/cdr/inbound" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="cdr"><CDR direction="inbound" /></RoleGuard>} />
              <Route path="/cdr/phone/:phone" element={<RoleGuard allow={["admin", "reseller", "customer"]} menu="cdr"><CdrPhoneDetail /></RoleGuard>} />
              <Route path="/cdr/reseller" element={<RoleGuard allow={["admin"]} menu="cdr-reseller"><CDR channel="reseller" /></RoleGuard>} />
              <Route path="/settings" element={<RoleGuard allow={["admin"]} menu="settings"><SystemSettings /></RoleGuard>} />
              <Route path="/audios" element={<RoleGuard allow={["admin"]} menu="audios"><AudioLibrary mode="admin" /></RoleGuard>} />
              <Route path="/branding" element={<RoleGuard allow={["admin"]} menu="branding"><AdminBranding /></RoleGuard>} />
              <Route path="/system-logs" element={<RoleGuard allow={["admin"]} menu="system-logs"><SystemLogs /></RoleGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <AppToaster />
        </BrowserRouter>
        </BrandProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
